import { useCallback, useMemo } from "react";

import { Formik } from "formik";
import { Button, Form } from "reactstrap";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { apiSlice } from "app/apiSlice";
import LabelledData from "components/Forms/LabelledData";
import { ConfirmLoseUnsavedFormik } from "components/Forms/components/ConfirmLoseUnsavedFormik";
import { getInitialValues } from "components/Forms/helpers/getInitialValues";
import Loader from "components/Loader";
import Panel from "components/Panel";
import PanelBody from "components/Panel/PanelBody";
import PanelFooter from "components/Panel/PanelFooter";
import { showToast } from "components/Toasts/helpers/showToast";
import { campaignDataDefs } from "features/campaigns/constants/campaignDataDefs";
import { getChangedValues } from "helpers/getChangedValues";
import useAuthorizationCheck from "hooks/useAuthorizationCheck";

export const Campaign = ({
  campaign_id,
  isEditing,
  setIsEditing,
  disableEditing,
  ...props
}) => {
  const { data, isFetching } = apiSlice.useGetCampaignQuery({
    _id: campaign_id,
  });

  const [updateCampaign, { isLoading: isLoadingUpdate }]
    = apiSlice.useUpdateCampaignMutation();

  const canMakePayment = useAuthorizationCheck(["make_payment"]);
  const isLoading = isFetching || isLoadingUpdate;

  const campaignDefs = useMemo(
    () => campaignDataDefs(data?.result, { canMakePayment }),
    [data?.result, canMakePayment],
  );
  const initialValues = useMemo(
    () => getInitialValues(campaignDefs?.body, data?.result),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignDefs?.body, data?.result, isEditing],
  );

  const validate = useCallback(
    (values) => {
      const errors = {};

      campaignDefs?.body?.forEach((field) => {
        // TODO #campaignLanding: Validate that landing page content exists if enableLandingPage is true
        if (field?.editing?.required && !values[field?.fieldKey]) {
          errors[field?.fieldKey] = `Required`;
        }
        if (!values.name) {
          errors.name = "Required";
        }
        if (!values.status) {
          errors.status = "Required";
        }
        if (!values.brand_id) {
          errors.brand_id = "Required";
        }
        if (values?.enableAccountSegmentation && !values.department) {
          errors.department = "Required";
        }
        if (values.shortCode && !/^[a-zA-Z0-9]{3}$/.test(values.shortCode)) {
          errors.shortCode
            = "Short code must be exactly 3 characters and contain only letters and numbers";
        }
        // Validate segmentedNetPercent is between 0 and 100
        if (
          values.segmentedNetPercent !== undefined
          && values.segmentedNetPercent !== null
        ) {
          const netPercent = Number(values.segmentedNetPercent);
          if (isNaN(netPercent) || netPercent < 0 || netPercent > 100) {
            errors.segmentedNetPercent
              = "Net percentage must be between 0 and 100";
          }
        }

        if (
          values.department === "talent"
          && !values.enableAccountSegmentation
          && !canMakePayment
        ) {
          errors.enableAccountSegmentation
            = "Account segmentation is required for Talent campaigns";
        }

        if (
          initialValues?.enableAccountSegmentation
          && !values.enableAccountSegmentation
          && !canMakePayment
        ) {
          errors.enableAccountSegmentation
            = "Cannot disable account segmentation without payment permissions";
        }
      });

      return errors;
    },
    [
      campaignDefs?.body,
      initialValues?.enableAccountSegmentation,
      canMakePayment,
    ],
  );

  const onSubmit = useCallback(
    async(values, actions) => {
      try {
        const changedValues = getChangedValues(values, initialValues);
        if (values.logoFile === null && data?.result?.logoFileUrl) {
          changedValues.deleteLogoFile = true;
        }
        if (Object.keys(changedValues).length) {
          await updateCampaign({
            _id: data?.result?._id,
            ...changedValues,
          }).unwrap();
        }
        showToast({
          type: "success",
          message: "Campaign saved successfully.",
        });
        actions.resetForm();
        setIsEditing(false);
      } catch (error) {
        showToast({
          type: "error",
          message: `Error updating campaign. ${error?.data?.message || error?.message}`,
        });
        console.error("Error updating campaign", error);
      }
      actions.setSubmitting(false);
    },
    [
      data?.result?._id,
      data?.result?.logoFileUrl,
      initialValues,
      setIsEditing,
      updateCampaign,
    ],
  );

  return (
    <div className="campaign-details details-panel d-flex">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
        enableReinitialize={true}
      >
        {({ dirty, isSubmitting, ...props }) => (
          <Form onSubmit={props.handleSubmit}>
            <Panel>
              <PanelBody padded={true} scrollable={true} borders={false}>
                {isLoading && <Loader />}

                {campaignDefs?.body?.map((def, i) => {
                  if (def?.role === "section") {
                    return (
                      <div key={i} className="labeled-data-container">
                        {def?.label && (
                          <h2 className="fs-5 section-label text-muted">
                            {def?.label}
                          </h2>
                        )}
                        {def?.fields?.map((subDef, j) => {
                          return (
                            <LabelledData
                              key={j}
                              definition={subDef}
                              isEditing={isEditing}
                              data={data?.result}
                            />
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                })}
              </PanelBody>
              {isEditing && (
                <PanelFooter>
                  <ConfirmLoseUnsavedFormik />
                  <div className="save-form-footer d-flex align-items-center justify-content-end gap-3">
                    <Button
                      color="secondary"
                      outline
                      size="lg"
                      className="d-flex align-items-center"
                      onClick={() => disableEditing(dirty)}
                    >
                      <XMarkIcon height={20} width={20} className="me-2" />
                      Cancel
                    </Button>
                    <Button
                      color="dark"
                      className="d-flex align-items-center"
                      type="submit"
                      size="lg"
                      disabled={isSubmitting || !dirty}
                    >
                      <CheckIcon height={20} width={20} className="me-2" />
                      Save
                    </Button>
                  </div>
                </PanelFooter>
              )}
            </Panel>
          </Form>
        )}
      </Formik>
    </div>
  );
};
